import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Check, ShieldCheck, Zap, MessageCircle } from "lucide-react";
import api, { getErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NeonButton, NeonCard, SectionTitle, Pill } from "../components/Neon";
import { ReferralCard } from "../components/ReferralCard";
import { gbp } from "../lib/format";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/auth/verification-status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const toggleNeuro = async () => {
    setBusy(true);
    try {
      await api.put("/me/preferences", { neuro_features: !user?.neuro_features });
      await refreshUser();
      toast.success(!user?.neuro_features ? "Quick Buy mode enabled." : "Quick Buy mode turned off.");
    } catch (e) { toast.error(getErr(e, "Could not update preferences")); }
    finally { setBusy(false); }
  };

  const toggleWhatsapp = async () => {
    setBusy(true);
    try {
      await api.put("/me/preferences", { whatsapp_opt_in: !user?.whatsapp_opt_in });
      await refreshUser();
      toast.success(!user?.whatsapp_opt_in ? "WhatsApp updates enabled." : "WhatsApp updates turned off.");
    } catch (e) { toast.error(getErr(e, "Could not update preferences")); }
    finally { setBusy(false); }
  };

  const verifyEmail = async () => {
    if (!status?.email_link) { toast.error("No verification link available."); return; }
    setBusy(true);
    try {
      await fetch(status.email_link, { method: "GET", mode: "no-cors" });
      await new Promise((r) => setTimeout(r, 800));
      await loadStatus(); await refreshUser();
      toast.success("Email verification triggered. Refreshed status.");
    } catch (e) { toast.error("Could not verify email automatically. Open the link manually."); }
    finally { setBusy(false); }
  };

  const verifyPhone = async () => {
    if (!code.trim()) { toast.error("Enter the code sent to your phone."); return; }
    setBusy(true);
    try {
      await api.post("/auth/verify-phone", { code: code.trim() });
      toast.success("Phone verified!");
      setCode(""); loadStatus(); refreshUser();
    } catch (e) { toast.error(getErr(e, "Invalid code")); } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/resend-verification"); setStatus((s) => ({ ...s, ...data })); toast.success("Verification resent."); }
    catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const emailVerified = status?.email_verified;
  const phoneVerified = status?.phone_verified;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10" data-testid="profile-verification-status">
      <SectionTitle accent="white">Profile &amp; Verification</SectionTitle>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        <NeonCard variant="flow" innerClassName="p-6">
          <p className="font-display uppercase text-xl text-white">Your Details</p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[var(--text-muted)]">Name</span><span className="text-white">{user?.full_name}</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-muted)]">Email</span><span className="text-white">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-muted)]">Phone</span><span className="text-white">{user?.mobile_phone}</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-muted)]">DOB</span><span className="text-white">{user?.date_of_birth}</span></div>
            <div className="flex justify-between"><span className="text-[var(--text-muted)]">Wallet</span><span className="glow-green">{gbp(user?.wallet_balance)}</span></div>
            <div className="flex justify-between items-center"><span className="text-[var(--text-muted)]">Age (18+)</span><Pill color={user?.age_verified ? "green" : "pink"}>{user?.age_verified ? "Verified" : "Unverified"}</Pill></div>
          </div>
        </NeonCard>

        <NeonCard variant="flow" innerClassName="p-6">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[var(--neon-green)]" /><p className="font-display uppercase text-xl text-white">Verification</p></div>
          <p className="mt-1 text-xs text-[var(--text-muted)]">Required before entering competitions or subscribing.</p>

          {/* Email */}
          <div className="mt-4 rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-white"><Mail className="h-4 w-4 text-[var(--neon-cyan)]" /> Email</span>
              {emailVerified ? <Pill color="green"><Check className="h-3 w-3" /> Verified</Pill> : <Pill color="pink">Unverified</Pill>}
            </div>
            {!emailVerified && (
              <div className="mt-3">
                <NeonButton variant="outlineCyan" className="w-full" onClick={verifyEmail} loading={busy} data-testid="verify-email-button">Verify Email</NeonButton>
                {status?.email_delivered === false && status?.email_link && (
                  <p className="mt-2 text-[11px] text-[var(--text-muted)] break-all">Email delivery is off in this environment. <a href={status.email_link} target="_blank" rel="noreferrer" className="text-[var(--neon-cyan)] underline">Open verification link</a></p>
                )}
              </div>
            )}
          </div>

          {/* Phone */}
          <div className="mt-3 rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-white"><Phone className="h-4 w-4 text-[var(--neon-cyan)]" /> Phone</span>
              {phoneVerified ? <Pill color="green"><Check className="h-3 w-3" /> Verified</Pill> : <Pill color="pink">Unverified</Pill>}
            </div>
            {!phoneVerified && (
              <div className="mt-3 flex gap-2">
                <input data-testid="verify-phone-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="flex-1 bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-[var(--neon-cyan)]" />
                <NeonButton variant="outlineGreen" onClick={verifyPhone} loading={busy} data-testid="verify-phone-button">Verify</NeonButton>
              </div>
            )}
            {!phoneVerified && status?.sms_delivered === false && status?.phone_code && (
              <p className="mt-2 text-[11px] text-[var(--text-muted)]">SMS delivery is off in this environment. Your code: <span className="text-[var(--neon-green)] font-mono">{status.phone_code}</span></p>
            )}
          </div>

          {(!emailVerified || !phoneVerified) && (
            <NeonButton variant="ghost" className="mt-3" onClick={resend} loading={busy} data-testid="resend-verification-button">Resend verification</NeonButton>
          )}
        </NeonCard>
      </div>

      {/* Accessibility / Quick Buy preference */}
      <NeonCard variant="flow" className="mt-5" innerClassName="p-6" data-testid="profile-preferences">
        <div className="flex items-center gap-2"><Zap className="h-5 w-5 text-[var(--neon-cyan)]" /><p className="font-display uppercase text-xl text-white">Quick Buy &amp; Accessibility</p></div>
        <label className="mt-4 flex items-start justify-between gap-4 rounded-xl border border-[rgba(0,229,255,.3)] bg-[rgba(0,229,255,.05)] px-4 py-3 cursor-pointer hover:border-[rgba(0,229,255,.5)] transition-colors">
          <span className="text-sm">
            <span className="block font-display uppercase tracking-wide text-[var(--neon-cyan)]">Neurodivergent-friendly Quick Buy</span>
            <span className="mt-0.5 block text-xs text-[var(--text-muted)]">Fewer steps, clearer focus and one-tap entry on competition pages. The skill question always stays (it's legally required).</span>
          </span>
          <input type="checkbox" data-testid="profile-neuro-toggle" checked={!!user?.neuro_features} disabled={busy} onChange={toggleNeuro} className="mt-1 h-5 w-5 shrink-0 accent-[var(--neon-cyan)]" />
        </label>

        <label className="mt-3 flex items-start justify-between gap-4 rounded-xl border border-[rgba(57,255,20,.3)] bg-[rgba(57,255,20,.05)] px-4 py-3 cursor-pointer hover:border-[rgba(57,255,20,.5)] transition-colors">
          <span className="text-sm">
            <span className="flex items-center gap-1.5 font-display uppercase tracking-wide text-[var(--neon-green)]"><MessageCircle className="h-4 w-4" /> WhatsApp updates</span>
            <span className="mt-0.5 block text-xs text-[var(--text-muted)]">Get win alerts and the odd offer on WhatsApp. Standard messaging rates may apply — you can opt out anytime.</span>
          </span>
          <input type="checkbox" data-testid="profile-whatsapp-toggle" checked={!!user?.whatsapp_opt_in} disabled={busy} onChange={toggleWhatsapp} className="mt-1 h-5 w-5 shrink-0 accent-[var(--neon-green)]" />
        </label>
      </NeonCard>

      {/* Refer a friend */}
      <ReferralCard />
    </div>
  );
}
